import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { ThemeProvider } from './context/ThemeContext';
import LoadingOverlay from './components/LoadingOverlay';

// Layout components
import Layout from './components/Layout';
import BusinessSelector from './components/BusinessSelector';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Categories from './pages/Categories';
import Products from './pages/Products';
import Purchases from './pages/Purchases';
import Orders from './pages/Orders';
import Invoices from './pages/Invoices';
import Customers from './pages/Customers';
import Employees from './pages/Employees';
import Roles from './pages/Roles';
import Permissions from './pages/Permissions';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import StockManagement from './pages/StockManagement';
import Terminals from './pages/Terminals';
import DataExplorer from './pages/DataExplorer';
import NotFound from './pages/NotFound';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, selectedHierarchy } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // If user is authenticated but hasn't selected a business hierarchy, redirect to selector
  if (!selectedHierarchy) {
    return <Navigate to="/select-business" replace />;
  }

  return children;
};

// App content component that uses loading context
const AppContent = () => {
  const { currentUser, loading } = useAuth();
  const { isLoading, loadingMessage } = useLoading();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Set isReady to true once auth state is determined
    if (!loading) {
      setIsReady(true);
    }
  }, [loading]);

  if (!isReady) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-container">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={currentUser ? <Navigate to="/" /> : <Login />} />

        {/* Business Selector Route */}
        <Route
          path="/select-business"
          element={currentUser ? <BusinessSelector /> : <Navigate to="/login" />}
        />

        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="categories" element={<Categories />} />
          <Route path="products" element={<Products />} />
          <Route path="stock-management" element={<StockManagement />} />
          <Route path="purchases" element={<Purchases />} />
          <Route path="orders" element={<Orders />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="customers" element={<Customers />} />
          <Route path="employees" element={<Employees />} />
          <Route path="roles" element={<Roles />} />
          <Route path="permissions" element={<Permissions />} />
          <Route path="reports" element={<Reports />} />
          <Route path="terminals" element={<Terminals />} />
          <Route path="data-explorer" element={<DataExplorer />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Global Loading Overlay */}
      <LoadingOverlay open={isLoading} message={loadingMessage} />
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LoadingProvider>
        <AppContent />
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App;
